import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { normalizePhoneNumber, formatPhoneDisplay, getPhoneValidationError } from '../utils/phoneUtils';
import './UserSettings.css';

function UserSettings({ user }) {
  // Add navigate for redirection
  const navigate = useNavigate();
  
  // Add ref for cleanup
  const redirectTimeoutRef = useRef(null);
  
  // Personal information state
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  
  // Preferences state
  const [amazonAccount, setAmazonAccount] = useState('');
  const [soundPreference, setSoundPreference] = useState('stereo');
  
  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  // Fetch user settings when component mounts
  useEffect(() => {
    const fetchUserSettings = async () => {
      try {
        setIsLoading(true);
        console.log('Fetching user settings for user:', user);
        
        // First, set sensible defaults from the user object
        setName(user?.name || '');
        setEmail(user?.email || '');
        
        // Then try to fetch additional settings
        const response = await fetch('/api/user/settings', {
          credentials: 'include'
        });
        
        if (!response.ok) {
          // If the endpoint isn't available yet, just use the user object
          if (response.status === 404) {
            console.log('Settings endpoint not available, using default values');
            setIsLoading(false);
            return;
          }
          throw new Error('Failed to fetch user settings');
        }
        
        const data = await response.json();
        console.log('Received settings:', data);
        
        // Update state with fetched data
        setName(data.name || user?.name || '');
        setEmail(data.email || user?.email || '');
        // Format phone for display if it exists
        setPhone(data.phone ? formatPhoneDisplay(data.phone) : '');
        setAmazonAccount(data.amazon_account || '');
        setSoundPreference(data.sound_preference || 'stereo');
        
      } catch (err) {
        console.error('Error fetching user settings:', err);
        setError('Failed to load your settings. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUserSettings();
  }, [user]);

  // Handle phone number change
  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setPhone(value);
    
    // Validate phone number
    const error = getPhoneValidationError(value);
    setPhoneError(error || '');
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setPhoneError('');
    
    // Validate phone before submitting
    const phoneValidationError = getPhoneValidationError(phone);
    if (phoneValidationError) {
      setPhoneError(phoneValidationError);
      return;
    }
    
    setIsSaving(true);
    
    try {
      // Normalize phone number before saving (store as digits only)
      const normalizedPhone = phone ? normalizePhoneNumber(phone) : '';
      
      console.log('Saving settings:', { name, email, phone: normalizedPhone, amazonAccount, soundPreference });
      
      const response = await fetch('/api/user/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name,
          email,
          phone: normalizedPhone, // Save normalized phone
          amazon_account: amazonAccount,
          sound_preference: soundPreference
        }),
      });
      
      if (!response.ok) {
        // If the endpoint isn't implemented yet, show a success message anyway
        if (response.status === 404) {
          console.log('Settings endpoint not available, simulating success');
          setSuccessMessage('Settings updated successfully');
          
          // Set up redirect after 1 second
          redirectTimeoutRef.current = setTimeout(() => {
            navigate('/dashboard');
          }, 1000);
          
          setIsSaving(false);
          return;
        }
        
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update settings');
      }
      
      setSuccessMessage('Settings updated successfully');
      
      // Set up redirect after 1 second
      redirectTimeoutRef.current = setTimeout(() => {
        navigate('/dashboard');
      }, 1000);
      
    } catch (err) {
      console.error('Error updating settings:', err);
      setError(err.message || 'An error occurred while saving your settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="settings-loading">Loading your settings...</div>;
  }

  return (
    <div className="settings-container">
      <h2>User Settings</h2>
      
      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}
      
      <form onSubmit={handleSubmit}>
        <div className="settings-section">
          <h3>Personal Information</h3>
          
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              value={phone}
              onChange={handlePhoneChange}
              placeholder="(123) 456-7890"
              className={phoneError ? 'error' : ''}
            />
            {phoneError && <span className="field-error">{phoneError}</span>}
            <span className="field-hint">US and Canada numbers only</span>
          </div>
        </div>
        
        <div className="settings-section">
          <h3>Preferences</h3>
          
          <div className="form-group">
            <label htmlFor="amazonAccount">Amazon Account (for Alexa integration)</label>
            <input
              type="text"
              id="amazonAccount"
              value={amazonAccount}
              onChange={(e) => setAmazonAccount(e.target.value)}
              placeholder="your.email@example.com"
            />
          </div>
          
          <div className="form-group">
            <label>Sound Preference</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="soundPreference"
                  value="stereo"
                  checked={soundPreference === 'stereo'}
                  onChange={() => setSoundPreference('stereo')}
                />
                Stereo
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="soundPreference"
                  value="mono"
                  checked={soundPreference === 'mono'}
                  onChange={() => setSoundPreference('mono')}
                />
                Mono
              </label>
            </div>
          </div>
        </div>
        
        <div className="settings-actions">
          <button 
            type="submit" 
            className="settings-button save-button"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default UserSettings;